import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useMutation, useLazyQuery, useQuery } from '@apollo/react-hooks';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import styled, { createGlobalStyle } from 'styled-components';
import { IconEnum as Icons } from '../Icons';
import BaseModal, { ModalList } from '../modals/BaseModal';
import {
  GET_CONTACTS,
  GET_CONTACTS_WITHOUT_REFERRAL,
  GET_DASHBOARD_STAT_CONTACTS,
  GET_REFFERED_CONTACTS,
  SEND_WELCOME_TEXT,
  GET_CURRENT_ORGANIZATION,
} from '../../graphql/queries';
import CustomerTable from './CustomerTable';
import { AppContext } from '../../context/AppContext';
import * as Price from '../../util/Price';
import * as Time from '../../util/Time';
import Section from '../../shared/Section';
import Container from '../../shared/Container';
import { useMobileMedia } from '../../util/MediaQuery';
import ContactsList from './mobile_ui/ContactsList';
import Icon from '../Icon';
import colors from '../../styles/colors';
import { SEARCH_TYPES } from '../../util/customersUtil';
import analyticsKeys from '../../util/UsageStatLinks';
import useMutateAnalytics from '../../util/mutate.Analytics';

// Csv file header - base headers that are always shown
const baseHeaders = [
  { label: 'PHONE', key: 'phoneNumber' },
  { label: 'CREATED AT', key: 'createdAt' },
  { label: 'LAST IMPORT', key: 'lastImported' },
  { label: 'ORIGINAL SOURCE', key: 'originalSource' },
  { label: 'OPT-IN METHOD', key: 'optInMethod' },
  { label: 'LAST OPT-IN', key: 'lastOptIn' },
  { label: 'OPT-OUT', key: 'optOut' },
];

// Referral marketing headers that are conditionally shown
const referralHeaders = [
  { label: 'IN WELCOME QUEUE', key: 'inWelcomeQueue' },
  { label: 'CURRENT AVAILABLE BALANCE', key: 'currentAvailableBalance' },
  { label: 'TOTAL CREDIT GRANTED', key: 'totalCreditGranted' },
  { label: 'TOTAL CREDIT REDEEMED', key: 'totalCreditRedeemed' },
  { label: 'TOTAL DISCOUNTS GRANTED', key: 'totalCouponsGranted' },
  { label: 'TOTAL DISCOUNTS REDEEMED', key: 'totalCouponsRedeemed' },
  { label: 'TOTAL REF DISCOUNTS ACCEPTED', key: 'totalCouponsAcceptedRef' },
  { label: 'TOTAL REF CLICKS', key: 'totalRefClicks' },
  { label: 'TOTAL REF DISCOUNTS REDEEMED', key: 'totalCouponsRedRef' },
  { label: 'TOTAL REF CONVERSION RATE', key: 'totalConversionRateRef' },
];

// SMS campaign headers that are conditionally shown
const smsCampaignHeaders = [
  { label: 'SMS OPT-OUT', key: 'smsCampaignOptOut' },
];

// Function to get headers based on referralMarketingEnabled and smsCampaignsEnabled
export const getHeaders = (referralMarketingEnabled, smsCampaignsEnabled) => {
  const headers = [];

  // Base headers (first 3)
  headers.push(...baseHeaders.slice(0, 3)); // PHONE, CREATED AT, LAST IMPORT

  // Referral headers if enabled
  if (referralMarketingEnabled) {
    headers.push(...referralHeaders);
  }

  // Remaining base headers (ORIGINAL SOURCE, OPT-IN METHOD, LAST OPT-IN, OPT-OUT)
  headers.push(...baseHeaders.slice(3, 7));

  // SMS campaign headers if enabled
  if (smsCampaignsEnabled) {
    headers.push(...smsCampaignHeaders);
  }

  return headers;
};

const BreadcrumbContainer = styled.div`
  width: 100%;
  overflow: auto;
  padding: 15px;
  display: flex;
  border-bottom: 1px solid ${colors.grey6};
  align-items: center;
  justify-content: ${(props) => props.justify || 'flex-start'};
  background: white;
  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }
  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: grey;
    border-radius: 10px;
  }
  @media screen and (max-width: 450px) {
    padding: 15px 0 15px 10px;
  }
`;

const BreadcrumbWrapper = styled.ul`
  display: flex;
  align-items: center;
  list-style-type: none;
  max-height: 20px;
  padding: ${(props) => props.padding || '0'};
`;

const BreadCrumbItem = styled.li`
  display: inline-flex;
  align-items: center;
  margin: 0 0 0 10px;
  font-size: 13px;
  cursor: ${(props) => (props.clickable === '1' ? 'pointer' : null)};
  color: ${(props) => (props.clickable === '1' ? colors.seaGreen : null)};
  & :first-child {
    margin: 0;
  }
`;

const BreadcrumbHeading = styled.li`
  font-size: 15px;
  padding: 0 10px 0 10px;
  position: sticky;
  left: -15px;
  background: white;
  cursor: pointer;
  z-index: 5;
  align-items: center;
  display: flex;
  cursor: ${(props) => (props.onClick ? 'pointer' : 'unset')};
  @media screen and (max-width: 450px) {
    left: -15px;
    padding: 0 10px 0 10px;
  }
`;

const BreadcrumbClearButton = styled.li`
  font-size: 15px;
  padding: 0 25px 0 10px;
  position: sticky;
  right: -16px;
  background: white;
  cursor: pointer;
  z-index: 5;
  align-items: center;
  display: flex;
  @media screen and (max-width: 450px) {
    right: 0;
    padding: 0 5px 0 10px;
  }
`;

const TooltipStyles = createGlobalStyle`
.__react_component_tooltip{
      display: inline-block !important;
      max-width: 300px;
    };
    .css-12jo7m5, .css-yk16xz-control{
      font-size:13px !important;
    }
    
    .css-1rhbuit-multiValue{
      font-size:13px !important;
      background: rgba(26,188,156,0.1) !important;
      &>div{
        color: ${colors.seaGreen};
      };
    };
`;

export default function Customers() {
  //local states
  const { app, setApp } = useContext(AppContext);
  const [contactModalIsVisible, setContactModalIsVisible] = useState(false);
  const [searchModalIsVisible, setSearchModalIsVisible] = useState(false);
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [isMore, setIsMore] = useState(true);
  const [welcomeText, setWelcomeText] = useState('');
  const [allContactsData, setAllContactsData] = useState([]);
  const [allReferredContacts, setAllReferredContacts] = useState([]);
  const [filteredContactsData, setFilteredContactsData] = useState([]);
  const [contactRefFilter, setContactRefFilter] = useState({});
  const [referrers, setReferrers] = useState([]);
  const isMobile = useMobileMedia();
  const [fetching, setFetching] = useState(false);
  const [searchQuery, setSearchQuery] = useState({}); // for serach inside referred list
  const [refSearchData, setRefSearchData] = useState([]);
  const [metricsSearchData, setMetricsSearchData] = useState([]);
  const [getRefsQuery, setGetRefsQuery] = useState(app?.getRefsQuery);
  const [welcomeQue, setWelcomeQue] = useState(app?.welcomeQueue || []);
  const [scrollToTop, setScrollToTop] = useState(true);
  const [allMetricsContacts, setAllMetricsContacts] = useState([]);
  const [tagsFilteredContacts, setTagsFilteredContacts] = useState([]);
  const mutateAnalytics = useMutateAnalytics();
  const [tagsFilterModal, setTagsFilterModal] = useState(false);
  const [contactsCategory, setContactsCategory] = useState(null);
  const [ordering, setOrdering] = useState({
    fieldName: 'phoneNumber',
    order: 'asc',
  });
  const csvRef = useRef();
  const breadcrumbRef = useRef();

  const { data: orgData } = useQuery(GET_CURRENT_ORGANIZATION, {
    fetchPolicy: 'cache-first',
  });

  const referralMarketingEnabled = orgData?.getCurrentOrganization?.referralMarketingEnabled ?? true;
  const smsCampaignsEnabled = !!(orgData?.getCurrentOrganization?.smsCampaign?.OptInAt);

  const pagination = {
    limit: 50,
    offset: 1,
  };

  const [getContacts, {
    data, loading, refetch, fetchMore,
  }] = useLazyQuery(
    referralMarketingEnabled ? GET_CONTACTS : GET_CONTACTS_WITHOUT_REFERRAL,
    {
      fetchPolicy: 'network-only',
      variables: {
        query: {
          phoneNumber: app?.searchQuery?.phoneNumber || null,
        },
        ordering: { fieldName: 'phoneNumber', order: 1 },
      },
      onCompleted(data) {
        initialStatePaging(data?.getContacts);
        setApp((oldApp) => ({ ...oldApp, refetchContacts: getContacts }));
      },
    },
  );

  const [
    getRefferedContacts,
    { data: refData, refFetchMore, loading: refLoading },
  ] = useLazyQuery(GET_REFFERED_CONTACTS, {
    fetchPolicy: 'network-only',
    variables: getRefsQuery || contactRefFilter,
    onCompleted(data) {
      setApp((oldApp) => ({
        ...oldApp,
        paginationOffset: 1,
        getRefsQuery: false,
      }));
      initialStatePaging(data?.getReferedContacts);
      setFetching(false);
    },
  });

  const [
    getDashboardStatContacts,
    { data: statContacts, loading: statLoading },
  ] = useLazyQuery(GET_DASHBOARD_STAT_CONTACTS, {
    onCompleted(data) {
      setApp((oldApp) => ({
        ...oldApp,
        paginationOffset: 1,
        getRefsQuery: false,
      }));
      initialStatePaging(data?.getDashboardStatContacts);

      setFetching(false);
    },
  });

  useEffect(() => {
    if (
      exportData.length > 0
      && csvRef
      && csvRef.current
      && csvRef.current.link
    ) {
      setTimeout(() => {
        csvRef.current.link.click();
        setExportData([]);
      });
    }
  }, [exportData]);

  // Download csv file
  const downloadExportData = () => {
    if (allContactsData?.length > 0 && csvRef?.current?.link?.click) {
      csvRef.current.link.click();
    }
  };

  const showCsvLink = !(app?.getMetricesQuery || referrers.length > 0);

  // TODO: clean up
  const getToolBarButtons = () => {
    return [
      {
        icon: Icons.SearchRegular,
        onClick: () => {
          setContactsCategory(null);
          if (app?.searchQuery?.phoneNumber) {
            setApp((oldApp) => ({
              ...oldApp,
              searchQuery: { phoneNumber: null },
            }));
          }

          setSearchModalIsVisible(true);
        },
        text: 'Search',
      },
      {
        icon: Icons.FilterList,
        onClick: () => {
          if (app?.searchQuery?.phoneNumber) {
            setApp((oldApp) => ({
              ...oldApp,
              searchQuery: { phoneNumber: null },
            }));
          }
          setTagsFilterModal(true);
        },
        text: 'Filter By Tags',
      },
      {
        icon: Icons.PlusRegular,
        onClick: () => {
          setContactModalIsVisible(true);
        },
        text: 'Add Contacts',
        dataHelp: 'contacts-add-contacts',
      },

      ...(!isMobile && referralMarketingEnabled
        ? [
          {
            icon: Icons.PaperPlaneRegular,
            onClick: () => {
              setFetching(true);
              const mutateAnalyticsVariables = {
                link: analyticsKeys.CONTACTS_SEND_WELCOMES,
              };
              mutateAnalytics(mutateAnalyticsVariables);
              sendWelcomeText({
                variables: {
                  phoneNumbers: app?.sendAllWelcomes ? [] : app?.welcomeQueue,
                },
              });
            },
            text: welcomeText,
          },
        ]
        : []),
      ...(showCsvLink
        ? [
          {
            icon: Icons.FileExcel,
            onClick: () => {
              const mutateAnalyticsVariables = {
                link: analyticsKeys.CONTACTS_DOWNLOAD_CSV,
              };
              mutateAnalytics(mutateAnalyticsVariables);
              downloadExportData();
            },
            text: 'Download CSV',
          },
        ]
        : []),
    ];
  };

  // Perform sorting client side
  function dynamicsort(property, order) {
    let sort_order = 1;
    if (order === 'desc') {
      sort_order = -1;
    }
    return function (a, b) {
      // a should come before b in the sorted order
      if (a[property] < b[property]) {
        return -1 * sort_order;
        // a should come after b in the sorted order
      }
      if (a[property] > b[property]) {
        return 1 * sort_order;
        // a and b are the same
      }
      return 0 * sort_order;
    };
  }

  const handleSort = (columnName) => {
    setFetching(true);
    let contacts = [];
    const newOrderDirection = ordering.order === 'asc' ? 'desc' : 'asc';
    if (referrers.length > 0) {
      if (searchQuery?.type === SEARCH_TYPES.RefContacts) {
        contacts = refSearchData;
      } else if (searchQuery?.type === SEARCH_TYPES.TagsFiltered) {
        contacts = tagsFilteredContacts;
      } else {
        contacts = refData?.getReferedContacts;
      }
    } else if (searchQuery?.type === SEARCH_TYPES.TagsFiltered) {
      if (
        searchQuery?.type === SEARCH_TYPES.TagsFiltered
        && searchQuery?.phoneNumber
      ) {
        contacts = tagsFilteredContacts;
      } else {
        contacts = tagsFilteredContacts;
      }
    } else if (app.getMetricesQuery) {
      if (searchQuery?.type === SEARCH_TYPES.MetricesContacts) {
        contacts = metricsSearchData;
      } else {
        contacts = statContacts?.getDashboardStatContacts;
      }
    } else {
      contacts = data.getContacts;
    }
    const filter = contacts.sort(dynamicsort(columnName, newOrderDirection));
    setFilteredContactsData(
      paginate(filter, pagination.limit, pagination.offset),
    );
    setOrdering({ fieldName: columnName, order: newOrderDirection });
    setIsMore(true);
    setApp({ ...app, paginationOffset: 1 });
    setScrollToTop(!scrollToTop);
  };

  // Perform paging client side
  function setPaginate(array, page_size, page_number) {
    return array.slice((page_number - 1) * page_size, page_number * page_size);
  }
  const paginate = useCallback((array, page_size, page_number) => {
    return setPaginate(array, page_size, page_number);
  }, []);

  // Load moredata client side.
  const loadMoreData = () => {
    setApp({ ...app, paginationOffset: app.paginationOffset + 1 });
    let newData = [];
    if (referrers.length > 0) {
      newData = paginate(
        allReferredContacts,
        pagination.limit,
        app?.paginationOffset + 1,
      );
    } else if (app?.getMetricesQuery?.type) {
      newData = paginate(
        allMetricsContacts,
        pagination.limit,
        app?.paginationOffset + 1,
      );
    } else if (searchQuery?.type === SEARCH_TYPES.TagsFiltered) {
      newData = paginate(
        tagsFilteredContacts,
        pagination.limit,
        app?.paginationOffset + 1,
      );
    } else {
      newData = paginate(
        allContactsData,
        pagination.limit,
        app?.paginationOffset + 1,
      );
    }
    if (newData.length > 0) {
      const appendData = [...filteredContactsData, ...newData];
      setFilteredContactsData(appendData);
    } else {
      setIsMore(false);
    }
    setIsLoadingMoreData(true);
  };

  const initialStatePaging = useCallback(
    (contacts) => {
      setOrdering({ fieldName: 'phoneNumber', order: 'asc' });
      setApp((old) => ({ ...old, paginationOffset: 1 }));
      const filter = contacts.sort(
        dynamicsort(ordering.fieldName, ordering.order),
      );
      setFilteredContactsData(
        paginate(filter, pagination.limit, pagination.offset),
      );
      setIsMore(true);
    },
    [paginate, pagination.limit, pagination.offset, setApp] // eslint-disable-line
  );

  const getRefferedContactsClick = useCallback(
    (props) => {
      switch (props.couponType) {
      case 'redeemed': {
        const mutateAnalyticsVariables = {
          link: analyticsKeys.CONTACTS_TOTAL_REF_DISCOUNT_REDEEMED,
        };
        if (!app?.isClickedFromDashboard) {
          mutateAnalytics(mutateAnalyticsVariables);
        } else {
          setApp((oldApp) => ({ ...oldApp, isClickedFromDashboard: false }));
        }

        break;
      }
      case 'accepted': {
        const mutateAnalyticsVariables = {
          link: analyticsKeys.CONTACTS_TOTAL_REF_DISCOUNT_ACCEPTED,
        };
        if (!app?.isClickedFromDashboard) {
          mutateAnalytics(mutateAnalyticsVariables);
        } else {
          setApp((oldApp) => ({ ...oldApp, isClickedFromDashboard: false }));
        }
        break;
      }
      default: {
        console.log('default');
      }
      }
      setSearchQuery({});
      setFetching(true);
      setContactRefFilter(() => getRefsQuery || props);
      setOrdering({ fieldName: 'phoneNumber', order: 'asc' });
      getRefferedContacts();
      setReferrers((referrers) => [...referrers, getRefsQuery || props]);
      const breadCrumbContainer = breadcrumbRef?.current;
      if (breadCrumbContainer) {
        breadCrumbContainer.scrollLeft = breadCrumbContainer?.scrollWidth;
      }
    },
    [getRefferedContacts, getRefsQuery] // eslint-disable-line
  );

  const onBreadCrumbItemClick = (phoneNumber, couponType, i) => {
    setSearchQuery({});
    setContactRefFilter((oldData) => ({
      ...oldData,
      phoneNumber,
      couponType,
    }));
    getRefferedContacts();
    setReferrers((referrers) => [...referrers.filter((ref, ind) => ind <= i)]);
  };

  const clearBreadcrumb = () => {
    setReferrers([]);
    setSearchQuery({ type: SEARCH_TYPES.Clear });
    setContactRefFilter({});
    setApp((oldApp) => ({ ...oldApp, getMetricesQuery: false }));
    if (data?.getContacts?.length) {
      setFilteredContactsData(allContactsData);
    }
    getContacts();
  };

  const [sendWelcomeText] = useMutation(SEND_WELCOME_TEXT, {
    async onCompleted() {
      setFetching(false);
      setSearchQuery({ type: SEARCH_TYPES.Clear });
      setTimeout(() => {
        setApp({ ...app, welcomeQueue: [], sendAllWelcomes: false });
      }, 0);
    },
    onError(err) {
      setFetching(false);
      console.log(err);
    },
  });

  // handing context change here due to linting warnings
  useEffect(() => {
    if (app.welcomeQueue) {
      setWelcomeQue(app.welcomeQueue);
    }
  }, [app.welcomeQueue]);

  useEffect(() => {
    setWelcomeText(
      `Send Welcomes (${
        app.sendAllWelcomes ? 'uploaded today' : welcomeQue.length
      })`,
    );
  }, [welcomeText, app.sendAllWelcomes, welcomeQue]);

  useEffect(() => {
    if (refData) {
      const refContacts = refData?.getReferedContacts || [];
      initialStatePaging(refContacts);
      setAllReferredContacts(refContacts);
      setFetching(false);
    }
  }, [refData, initialStatePaging]);

  useEffect(() => {
    if (statContacts) {
      const metricsContacts = statContacts?.getDashboardStatContacts || [];
      initialStatePaging(metricsContacts);
      setAllMetricsContacts(metricsContacts);
      setFetching(false);
    }
  }, [statContacts, initialStatePaging]);

  useEffect(() => {
    if (data && !getRefsQuery) {
      const allContacts = data?.getContacts || [];
      initialStatePaging(allContacts);
      setAllContactsData(allContacts);
    }
  }, [data, initialStatePaging, getRefsQuery]);

  useEffect(() => {
    setContactRefFilter({});
    if (getRefsQuery) {
      getRefferedContactsClick(getRefsQuery);
      setGetRefsQuery(false);
    } else if (app?.getMetricesQuery) {
      setApp((oldApp) => ({
        ...oldApp,
        searchQuery: { ...app?.getMetricesQuery },
      }));
      const {
        type, subType, start, end,
      } = app.getMetricesQuery;
      getDashboardStatContacts({
        variables: {
          query: {
            type,
            subType,
            start,
            end,
          },
        },
      });
    } else {
      setApp((oldApp) => ({
        ...oldApp,
        searchQuery: { phoneNumber: null },
        getMetricesQuery: false,
        paginationOffset: 1,
      }));
      getContacts();
    }

    return () => {
      setTimeout(() => {
        setApp((oldApp) => ({
          ...oldApp,
          paginationOffset: 1,
          getRefsQuery: false,
          getMetricesQuery: false,
        }));
      }, 0);
    };
  }, []); // eslint-disable-line

  //code for managing searching & filter functionality

  useEffect(
    () => {
      if (
        searchQuery?.type === SEARCH_TYPES.AllContacts
        && searchQuery.phoneNumber
      ) {
        setOrdering({ fieldName: 'phoneNumber', order: 'asc' });
        setApp((oldApp) => ({
          ...oldApp,
          searchQuery: { phoneNumber: searchQuery.phoneNumber },
        }));
      } else if (searchQuery?.type === SEARCH_TYPES.RefContacts) {
        const oldRefs = [...allReferredContacts];
        const refSearchContacts = oldRefs.filter((item) => item.phoneNumber.includes(searchQuery.phoneNumber));
        initialStatePaging(refSearchContacts);
        setRefSearchData(refSearchContacts);
      } else if (searchQuery?.type === SEARCH_TYPES.MetricesContacts) {
        const oldRefs = [...allMetricsContacts];
        const metricsSearchContacts = oldRefs.filter((item) => item.phoneNumber.includes(searchQuery.phoneNumber));
        setMetricsSearchData(metricsSearchContacts);
        initialStatePaging(metricsSearchContacts);
      } else if (searchQuery?.type === SEARCH_TYPES.TagsFiltered) {
        const filterTags = searchQuery.tags;
        let contactToFilter = [];
        if (app.getMetricesQuery) {
          contactToFilter = allMetricsContacts;
        } else if (referrers.length) {
          contactToFilter = allReferredContacts;
        } else {
          contactToFilter = allContactsData;
        }

        const filteredContacts = contactToFilter.filter((currentContact) => {
          let contactMatched = false;
          filterTags.forEach((tag) => {
            if (currentContact.attachedTags.includes(tag)) {
              contactMatched = true;
            }
          });

          return contactMatched;
        });
        setTagsFilteredContacts(filteredContacts);
        setContactsCategory('tagsFiltered');
        initialStatePaging(filteredContacts);
        setTagsFilterModal(false);
      } else if (
        searchQuery?.type === SEARCH_TYPES.Clear
        && contactsCategory === 'tagsFiltered'
      ) {
        setOrdering({ fieldName: 'phoneNumber', order: 'asc' });
        if (referrers.length) {
          initialStatePaging(allReferredContacts);
        } else if (app.getMetricesQuery) {
          initialStatePaging(allMetricsContacts);
        } else {
          initialStatePaging(allContactsData);
        }
      } else if (
        searchQuery?.type === SEARCH_TYPES.Clear
        && referrers.length > 0
      ) {
        setOrdering({ fieldName: 'phoneNumber', order: 'asc' });
        initialStatePaging(refData?.getReferedContacts);
      } else if (
        searchQuery?.type === SEARCH_TYPES.Clear
        && app.getMetricesQuery
      ) {
        setOrdering({ fieldName: 'phoneNumber', order: 'asc' });
        initialStatePaging(statContacts?.getDashboardStatContacts);
      } else if (
        searchQuery.type === SEARCH_TYPES.Clear
        && referrers.length < 1
        && !app.getMetricesQuery
      ) {
        if (app?.searchQuery?.phoneNumber) {
          setApp((oldApp) => ({
            ...oldApp,
            searchQuery: { phoneNumber: null },
          }));
        } else {
          getContacts();
        }
      }
    },
    [
      searchQuery,
      allReferredContacts,
      initialStatePaging,
      refData,
      referrers,
      refetch,
      setApp,
    ] // eslint-disable-line
  );

  useEffect(() => {
    if (referrers.length > 0) {
      setApp((oldApp) => ({ ...oldApp, getMetricesQuery: false }));
    }
  }, [referrers, setApp]);

  useEffect(() => {
    setFetching(false);
  }, [filteredContactsData]);

  useEffect(() => {
    setFetching(refLoading);
  }, [refLoading]);

  useEffect(() => {
    setFetching(statLoading);
  }, [statLoading]);

  useEffect(() => {
    // console.log(contactsCategory, 'contactsCategory');
  }, [contactsCategory]);

  return (
    <>
      <BaseModal
        open={contactModalIsVisible}
        modalType={ModalList.ImportContactsModal}
        onClose={setContactModalIsVisible}
        setSearchQuery={setSearchQuery}
        setReferrers={setReferrers}
      />
      <BaseModal
        open={searchModalIsVisible}
        modalType={ModalList.SearchModal}
        onClose={setSearchModalIsVisible}
        referrers={referrers}
        setSearchQuery={setSearchQuery}
        setContactsCategory={setContactsCategory}
      />

      <BaseModal
        open={tagsFilterModal}
        modalType={ModalList.TagsFilterContacts}
        onClose={setTagsFilterModal}
        setSearchQuery={setSearchQuery}
        searchQuery={searchQuery}
      />
      <Container>
        <Section
          headerTitle="Contacts"
          headerDirection={isMobile ? 'column' : 'row'}
          toolbarItems={getToolBarButtons()}
          loading={loading}
          contentDirection="column"
        >
          <CSVLink
            data={allContactsData.map((row) => {
              const baseData = {
                phoneNumber: row.phoneNumber,
                createdAt: moment(row.createdAt * 1000).format('L'),
                lastImported: moment(row.lastImported * 1000).format('L'),
                originalSource: row.originalSource,
                optInMethod: row.optInMethod,
                lastOptIn: row?.lastOptIn && row.lastOptIn !== 0 ? moment(row.lastOptIn * 1000).format('L') : '',
                optOut: row?.optOut && row.optOut !== 0 ? moment(row.optOut * 1000).format('L') : '',
              };

              // Add SMS campaign opt-out if enabled
              if (smsCampaignsEnabled) {
                baseData.smsCampaignOptOut = row?.smsCampaignOptOut && row.smsCampaignOptOut !== 0 ? moment(row.smsCampaignOptOut * 1000).format('L') : '';
              }

              // Add referral marketing fields if enabled
              if (referralMarketingEnabled) {
                return {
                  ...baseData,
                  inWelcomeQueue: (Time.getStartOfDay(Time.now()) < row.lastImported && row.attendedOrganization) ? 'Yes' : 'No',
                  currentAvailableBalance: `$${Price.output(row.currentAvailableBalance, true)}`,
                  totalCreditGranted: `$${Price.output(row.totalCreditGranted, true)}`,
                  totalCreditRedeemed: `$${Price.output(row.totalCreditRedeemed, true)}`,
                  totalCouponsGranted: row.totalCouponsGranted,
                  totalCouponsRedeemed: row.totalCouponsRedeemed,
                  totalCouponsAcceptedRef: `$${Price.output(row.totalCouponsAcceptedRef, true)}`,
                  totalRefClicks: row.totalRefClicks || '0',
                  totalCouponsRedRef: `$${Price.output(row.totalCouponsRedRef, true)}`,
                  totalConversionRateRef: `${row.totalConversionRateRef}%`,
                };
              }
              return baseData;
            })}
            headers={getHeaders(referralMarketingEnabled, smsCampaignsEnabled)}
            filename="Contacts.csv"
            ref={csvRef}
            target="_self"
          />
          {referrers.length > 0 && (
            <BreadcrumbContainer ref={breadcrumbRef}>
              <BreadcrumbWrapper>
                <BreadcrumbHeading>Referrers:</BreadcrumbHeading>
                {referrers.map((item, i, arr) => {
                  const clickable = i !== arr.length - 1;
                  return (
                    <React.Fragment key={`${item._id}_${i}`}>
                      <BreadCrumbItem
                        clickable={clickable ? '1' : '0'}
                        onClick={
                          clickable
                            ? () => onBreadCrumbItemClick(
                              item.phoneNumber,
                              item.couponType,
                              i,
                            )
                            : null
                        }
                      >
                        {i > 0 && (
                          <Icon
                            margin="0 10px 0 0"
                            size="18px"
                            icon={Icons.RightArrowRegular}
                          />
                        )}
                        {item.phoneNumber}
                      </BreadCrumbItem>
                    </React.Fragment>
                  );
                })}
                <BreadcrumbClearButton>
                  <div onClick={() => clearBreadcrumb()}>
                    <Icon size={14} icon={Icons.TimesCircleSolid} />
                  </div>
                </BreadcrumbClearButton>
              </BreadcrumbWrapper>
            </BreadcrumbContainer>
          )}

          {app?.getMetricesQuery?.type && (
            <BreadcrumbContainer justify="flex-end" ref={breadcrumbRef}>
              <BreadcrumbWrapper padding="0 40px 0 0">
                <BreadcrumbHeading>
                  {app?.getMetricesQuery.type}
                </BreadcrumbHeading>
                {app?.getMetricesQuery?.subType && '-'}
                <BreadcrumbHeading>
                  {app?.getMetricesQuery.subType}
                </BreadcrumbHeading>
                <BreadcrumbHeading>
                  {app?.getMetricesQuery.end && app?.getMetricesQuery.start > 0
                    ? `${moment(app?.getMetricesQuery.start * 1000).format(
                      'L',
                    )} - ${moment(app?.getMetricesQuery.end * 1000).format(
                      'L',
                    )}`
                    : 'All Time'}
                </BreadcrumbHeading>
                <BreadcrumbClearButton>
                  <div onClick={() => clearBreadcrumb()}>
                    <Icon size={14} icon={Icons.TimesCircleSolid} />
                  </div>
                </BreadcrumbClearButton>
              </BreadcrumbWrapper>
            </BreadcrumbContainer>
          )}

          {isMobile ? (
            <ContactsList
              contacts={filteredContactsData}
              fetchMore={fetchMore}
              pagination={pagination}
              isMore={isMore}
              ordering={ordering}
              handleSort={handleSort}
              loadMoreData={loadMoreData}
              isLoadingMoreData={isLoadingMoreData}
              getRefferedContactsClick={getRefferedContactsClick}
              fetching={fetching}
              setFetching={setFetching}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              refFetchMore={fetchMore}
              scrollToTop={scrollToTop}
              referralMarketingEnabled={referralMarketingEnabled}
              smsCampaignsEnabled={smsCampaignsEnabled}
            />
          ) : (
            <CustomerTable
              contacts={filteredContactsData}
              fetchMore={fetchMore}
              pagination={pagination}
              isMore={isMore}
              ordering={ordering}
              handleSort={handleSort}
              loadMoreData={loadMoreData}
              isLoadingMoreData={isLoadingMoreData}
              getRefferedContactsClick={getRefferedContactsClick}
              fetching={fetching}
              setFetching={setFetching}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              refFetchMore={refFetchMore}
              scrollToTop={scrollToTop}
              referralMarketingEnabled={referralMarketingEnabled}
            />
          )}
        </Section>
      </Container>
      <TooltipStyles />
    </>
  );
}
